import Loader from 'components/shared/Loader'
import {
    Image,
    Wrapper,
    PriceWrapper,
    LabelWrapper,
    HeaderWrapper,
    OtherPriceWrapper,
    PriceColumnWrapper,
    LowestPriceWrapper,
    PriceBadge,
} from './styles'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Button from 'components/shared/Button'
import InlineTextList from 'components/shared/InlineTextList'
import AttractionDropdown from './AttractionDropdown'
import Price from 'components/shared/Price'
import { useResolutions } from 'store/resolutions'
import { FormattedMessage, useIntl, defineMessage } from 'react-intl'
import { DefaultTheme } from 'styled-components'
import { useMemo } from 'react'
import AttractionAlert from './AttractionAlert/AttractionAlert'
import { formatPrice } from 'helpers/helpers'

export interface AttractionProps {
    title?: string
    details: string[]
    price?: number
    otherPrice?: number
    lowestPrice?: number
    isPriceFrom: boolean
    pricePer?: 'person' | 'couple'
    isAvailableOnline?: boolean
    images: [string, string]
    creator?: JSX.Element
    handleModalOpening?: () => void
    isPriceLoading?: boolean
    isButtonLoading?: boolean
    isButtonDisabled?: boolean
    isDropdownOpen?: boolean
    handleButtonClick?: () => void
    isAdded?: boolean
    id?: string
    promotionDescription?: Record<'mobile' | 'desktop', string>
    theme?: DefaultTheme
    alert?: string
    hasDarkMode?: boolean
    hasSmallContentPaddingTop?: boolean
    promotionBox?: JSX.Element
    label?: string
    priceLabel?: string
}

const Attraction = ({
    isAvailableOnline = false,
    title,
    price,
    otherPrice,
    lowestPrice,
    details,
    isPriceFrom,
    pricePer,
    images,
    creator,
    handleModalOpening,
    isPriceLoading,
    isButtonLoading,
    isButtonDisabled,
    isDropdownOpen,
    handleButtonClick,
    isAdded,
    id,
    promotionDescription,
    theme,
    alert,
    hasDarkMode,
    hasSmallContentPaddingTop,
    promotionBox,
    label,
    priceLabel,
}: AttractionProps) => {
    const { isTablet } = useResolutions()
    const { formatMessage } = useIntl()

    const hasAlert = !!alert
    const hasLowestPrice = lowestPrice !== undefined

    const buttonLabel = () => {
        let label = defineMessage({
            id: 'global.btn.add',
            defaultMessage: 'Dodaj',
        })

        if (isDropdownOpen || isButtonLoading) {
            label = isAdded
                ? defineMessage({
                      id: 'global.btn.remove',
                      defaultMessage: 'Usuń',
                  })
                : defineMessage({
                      id: 'global.btn.cancel',
                      defaultMessage: 'Anuluj',
                  })
        }

        return formatMessage(label)
    }

    const buttonTestId = () => {
        const prefix = `${id}-`
        let base = 'button-add-start'

        if (isDropdownOpen || isButtonLoading) {
            base = isAdded ? 'button-delete-all' : 'button-cancel-all'
        }

        return `${prefix}${base}`
    }

    const contentPadding = useMemo(() => {
        if (hasAlert) return 4
        if (hasSmallContentPaddingTop) return 8
        return 16
    }, [hasAlert, hasSmallContentPaddingTop])

    const lowestPriceInfo = (
        <LowestPriceWrapper
            variant="bodyExtraSmall"
            color="grayscale_800"
            align="right"
        >
            <FormattedMessage
                id="order.discount.lowest_price_30_days_info"
                defaultMessage="Najniższa cena z 30 dni przed wprowadzeniem obniżki:"
            />{' '}
            {formatPrice(lowestPrice || 0, true)}
        </LowestPriceWrapper>
    )

    return (
        <Wrapper
            direction={['column', 'row']}
            align={['center', 'flex-start']}
            justify={['flex-start', 'space-between']}
            isAvailableOnline={isAvailableOnline}
            gap={[16, 0]}
            wrap="wrap"
            isFullWidth
            id={id}
            theme={theme}
            hasDarkMode={hasDarkMode}
        >
            {!!label && <LabelWrapper>{label}</LabelWrapper>}
            <Box
                direction={['column', 'row']}
                isFullWidth
                gap={[0, 40]}
                pr={[16, 40]}
                pl={[16, 0]}
                pt={[16, 0]}
                pb={[16, 0]}
                mt={label && isTablet ? 10 : 0}
            >
                <HeaderWrapper
                    direction={['row', 'row-reverse']}
                    align={['center', 'flex-start']}
                    justify={['space-between', 'flex-end']}
                    isFullWidth
                    pb={[16, 0]}
                    gap={[24, 40]}
                    hasDarkMode={hasDarkMode}
                >
                    <Box pt={[0, 24]} pb={handleModalOpening ? 0 : [20, 22]}>
                        <Text
                            variant="heading3"
                            mb={[4, contentPadding]}
                            color={hasDarkMode ? 'grayscale_200' : undefined}
                        >
                            {title}
                        </Text>

                        {hasAlert && !isTablet && (
                            <Box mb={12}>
                                <AttractionAlert alert={alert} />
                            </Box>
                        )}
                        {!isTablet && (
                            <InlineTextList
                                items={details}
                                variant="body"
                                color={
                                    hasDarkMode
                                        ? 'grayscale_200'
                                        : 'grayscale_800'
                                }
                            />
                        )}
                        {promotionDescription && (
                            <Box mt={[0, 4]} mb={[4, 0]}>
                                <Text variant="bodyStrong" color="primary_500">
                                    {isTablet
                                        ? promotionDescription.mobile
                                        : promotionDescription.desktop}
                                </Text>
                            </Box>
                        )}
                        {handleModalOpening && (
                            <Text
                                as="button"
                                variant="bodyStrong"
                                color={
                                    hasDarkMode
                                        ? 'primary_500'
                                        : 'secondary_500'
                                }
                                mt={[0, hasSmallContentPaddingTop ? 8 : 16]}
                                onClick={() => handleModalOpening()}
                                data-testid={`${id}-button-learn-more`}
                            >
                                <FormattedMessage
                                    id="global.btn.learn_more"
                                    defaultMessage="Dowiedz się więcej"
                                />
                            </Text>
                        )}
                    </Box>

                    <Image
                        src={isTablet ? images[0] : images[1]}
                        alt="attraction"
                    />
                </HeaderWrapper>
                {hasAlert && isTablet && (
                    <Box mt={14}>
                        <AttractionAlert alert={alert} />
                    </Box>
                )}
                <PriceColumnWrapper
                    direction={['row', 'column-reverse']}
                    align={[
                        (hasLowestPrice && !isTablet) ||
                        (isTablet && !hasLowestPrice)
                            ? 'center'
                            : 'flex-start',
                        'flex-end',
                    ]}
                    justify={['space-between', 'flex-start']}
                    isFullWidth={isTablet}
                    gap={16}
                    pt={[hasAlert ? 0 : 16, 24]}
                >
                    {isAvailableOnline ? (
                        <>
                            {hasLowestPrice &&
                                !isTablet &&
                                price !== undefined &&
                                lowestPriceInfo}
                            <Button
                                variant={(() => {
                                    if (isDropdownOpen)
                                        return hasDarkMode
                                            ? 'borderPrimary'
                                            : 'borderSecondary'
                                    else
                                        return hasDarkMode
                                            ? 'primary'
                                            : 'secondary'
                                })()}
                                hasArrow={false}
                                onClick={handleButtonClick}
                                isLoading={isButtonLoading}
                                isDisabled={isButtonDisabled}
                                theme={theme}
                                testId={buttonTestId()}
                            >
                                {buttonLabel()}
                            </Button>
                        </>
                    ) : (
                        <Box pt={[10, 0]} pb={[10, 0]}>
                            <Text
                                hasNoWrap
                                variant="body"
                                color="grayscale_900"
                                align="right"
                            >
                                <FormattedMessage
                                    id="upsells.available_on_site_caption"
                                    defaultMessage="Dostępne na miejscu"
                                />
                            </Text>
                        </Box>
                    )}

                    <PriceWrapper
                        direction="column-reverse"
                        align="flex-end"
                        mb={[hasLowestPrice ? 40 : 0, 0]}
                    >
                        {isPriceLoading ? (
                            <Loader />
                        ) : (
                            <>
                                {price !== undefined && (
                                    <>
                                        {otherPrice !== undefined && (
                                            <OtherPriceWrapper
                                                variant="body"
                                                color="grayscale_900"
                                                isStrikeThrough
                                            >
                                                {formatPrice(otherPrice, true)}
                                            </OtherPriceWrapper>
                                        )}
                                        <Price
                                            value={price}
                                            description={
                                                isPriceFrom
                                                    ? formatMessage({
                                                          id: 'global.price.label_from',
                                                          defaultMessage: 'od',
                                                      })
                                                    : undefined
                                            }
                                            per={pricePer}
                                            isCapitalized={true}
                                            color={
                                                hasDarkMode
                                                    ? 'primary_500'
                                                    : undefined
                                            }
                                        />
                                        {!!priceLabel && (
                                            <Box mt={[0, -16]} mb={[2, 16]}>
                                                <PriceBadge
                                                    variant="bodySmall"
                                                    color="primary_700"
                                                    hasNoWrap
                                                >
                                                    {priceLabel}
                                                </PriceBadge>
                                            </Box>
                                        )}
                                    </>
                                )}
                                {hasLowestPrice &&
                                    isTablet &&
                                    price !== undefined &&
                                    lowestPriceInfo}
                            </>
                        )}
                    </PriceWrapper>
                </PriceColumnWrapper>
            </Box>

            {isDropdownOpen && (
                <AttractionDropdown
                    creator={creator}
                    handleButtonClick={handleButtonClick}
                    theme={theme}
                    promotionBox={promotionBox}
                />
            )}
        </Wrapper>
    )
}

export default Attraction
